import { Grid, IGridItem } from "./Grid";
import { Widget } from "@/modules";
import { countBy } from "es-toolkit/compat";
import { GRID_CONFIG, WIDGET_SIZES, WIDGET_TYPE_ORDER } from "../config/grid-config";

class GridObjectFactory {
    constructor(private widgets: Widget[]) {}

    buildGrid() {
        const grid: IGridItem[][] = [];
        const maxCols = GRID_CONFIG.MAX_COLUMNS;
        let currentRow: IGridItem[] = [];
        let currentRowWidth = 0;
        let currentRowType: string | null = null;

        for (let i = 0; i < this.widgets.length; i++) {
            const widget = this.widgets[i];
            const size = GridObjectFactory.sizeByWidgetType(widget);
            const widgetColSpan = size?.colsSpan || 1;
            const widgetType = widget.type;

            // Check if widget fits in current row AND is same type (or row is empty)
            const canFitInRow = currentRowWidth + widgetColSpan <= maxCols;
            const isSameType = currentRowType === null || currentRowType === widgetType;

            if (canFitInRow && isSameType) {
                // Set row type if this is the first widget in the row
                if (currentRowType === null) {
                    currentRowType = widgetType;
                }

                const gridItem: IGridItem = {
                    id: String(widget.id),
                    data: widget,
                    colSpan: widgetColSpan,
                    colStart: currentRowWidth,
                };

                // Add the widget once to current row (no duplicates)
                currentRow.push(gridItem);
                currentRowWidth += widgetColSpan;
            } else {
                // Fill remaining space in current row with empty items
                this.fillRowWithEmptyItems(currentRow, currentRowWidth, maxCols);

                // Push current row to grid and start new row
                if (currentRow.length > 0) {
                    grid.push([...currentRow]);
                }

                // Start new row with current widget
                currentRow = [];
                currentRowWidth = 0;
                currentRowType = widgetType;

                const gridItem: IGridItem = {
                    id: String(widget.id),
                    data: widget,
                    colSpan: widgetColSpan,
                    colStart: 0,
                };

                // Add the widget once to new row (no duplicates)
                currentRow.push(gridItem);
                currentRowWidth = widgetColSpan;
            }
        }

        // Handle the last row
        if (currentRow.length > 0) {
            this.fillRowWithEmptyItems(currentRow, currentRowWidth, maxCols);
            grid.push([...currentRow]);
        }

        return grid;
    }

    private fillRowWithEmptyItems(row: IGridItem[], currentWidth: number, maxCols: number) {
        for (let col = currentWidth; col < maxCols; col++) {
            row.push(Grid.emptyItem(1, col));
        }
    }

    fromGridToMap(grid: IGridItem[][]) {
        return grid.flat().reduce((acc, item) => ({ ...acc, [item.id]: item.data }), {});
    }

    static sizeByWidgetType(widget: Widget) {
        return WIDGET_SIZES[widget.type as keyof typeof WIDGET_SIZES];
    }

    static placeNonPlacedWidgets(grid: IGridItem[][], widgets: Widget[]) {
        const _grid = new Grid(grid, GRID_CONFIG.AUTO_REMOVE_EMPTY_ROWS, GRID_CONFIG.MAX_COLUMNS);
        const nonPlacedWidgets = widgets.filter((widget) => !grid.some((row) => row.some((item) => item.id === String(widget.id))));

        // Always add new widgets at the bottom of the grid
        const getTargetRowForType = (): number => {
            return _grid.grid.length;
        };

        nonPlacedWidgets.forEach((widget) => {
            console.log("## NON PLACED WIDGET", widget);
            const targetRow = getTargetRowForType();
            _grid.addItemToRow(
                {
                    id: String(widget.id),
                    data: widget,
                    colSpan: GridObjectFactory.sizeByWidgetType(widget)?.colsSpan ?? 1,
                },
                targetRow
            );
        });

        return _grid.grid;
    }
    static fixGridRows(grid: IGridItem[][], maxCols: number) {
        const widgetRow: Record<number, number> = {}; //* <widgetId, rowIndex>
        // * FIrst remove the widgets that are placed in different rows (due a incorrect placement - *GRID entity ERROR)
        const result = grid.map((row, rowIndex) => {
            return row.map((el) => {
                if (el.empty) return el;
                if (widgetRow[Number(el?.id)] && widgetRow[Number(el?.id)] != rowIndex) {
                    return Grid.emptyItem(1, el.colStart);
                }
                widgetRow[Number(el?.id)] = rowIndex;
                return el;
            });
        });
        // * Then if the rows are longer than the maxCols, remove the empty spaces for fixing it and return the slice of the row that is not longer than the maxCols
        return result.map((row) => {
            if (row.length > maxCols) {
                return row.filter((el) => !el.empty).slice(0, maxCols);
            }
            return row;
        });
    }

    static fromDBToGrid(dbFormat: string[][], widgets: Widget[]) {
        // [ [ 1,23,23,23], [2,2,4,6]]...
        const grid: IGridItem[][] = [];

        // Create widget map and track placed widgets
        const widgetMap: Record<string, Widget> = widgets.reduce((acc, widget) => ({ ...acc, [widget.id]: widget }), {});
        const placedWidgetIds = new Set<string>();

        // Process existing grid layout from DB
        dbFormat.forEach((row) => {
            const _row: IGridItem[] = [];
            const widgetsSpan = countBy(row);

            row.forEach((widgetId) => {
                const widget = widgetMap?.[widgetId] || null;
                if (widget) {
                    const widgetColSpan = widgetsSpan[widgetId] ?? 1;
                    const gridItem: IGridItem = {
                        id: String(widget.id),
                        data: widget,
                        colSpan: widgetColSpan,
                        colStart: _row.length,
                    };

                    _row.push(gridItem);
                    placedWidgetIds.add(String(widget.id));
                } else {
                    // * If widget is not found, add an empty item
                    _row.push(Grid.emptyItem(1, _row.length));
                }
            });

            grid.push(Grid.devirtualizeRow(_row));
        });

        const placedWidgets = GridObjectFactory.placeNonPlacedWidgets(grid, Object.values(widgets));
        return GridObjectFactory.fixGridRows(placedWidgets, GRID_CONFIG.MAX_COLUMNS);
    }

    static fromGridToDB(grid: IGridItem[][]) {
        return grid.map((row) => {
            const virtualizedRow = Grid.virtualizeRow(row);
            const dbRow = virtualizedRow.map((item) => (item.empty ? null : item.data.id));

            // Fill row to maxCols with nulls if it's shorter
            while (dbRow.length < GRID_CONFIG.MAX_COLUMNS) {
                dbRow.push(null);
            }

            return dbRow;
        });
    }
}

export { GridObjectFactory };
